/**
 * Engine & Propulsion Configuration Section
 * Extracted from BasicInfoTab to maintain file size limits
 */
import React from 'react';
import * as AutoConfig from '../utils/vesselAutoConfig';
import PropulsionConfigurationSelector from './PropulsionConfigurationSelector';
import FireProtectionInformation from './FireProtectionInformation';
import BatterySystemRequirements from './BatterySystemRequirements';

const EnginePropulsionSection = ({ vesselConfig, updateConfig }) => {
  
  // Extract propulsion details from unified configuration or legacy fields
  const getPropulsionDetails = () => {
    if (vesselConfig.propulsionConfiguration) {
      const config = vesselConfig.propulsionConfiguration;
      const parts = config.split('_');
      
      return {
        propulsionType: parts[0] === 'hybrid' ? 'battery_hybrid' : 
                       parts[0] === 'battery' ? 'battery_electric' :
                       parts[0],
        enginePosition: parts.includes('outboard') ? 'outboard' : 'inboard',
        engineLocation: parts.includes('above') ? 'above_deck' : 
                        parts.includes('below') ? 'below_deck' : 'above_deck',
        method: parts.includes('shaft') ? 'shaft' : 
                parts.includes('jet') ? 'water_jet' :
                parts.includes('pod') ? 'pod_drive' : 
                parts.includes('outboard') ? 'outboard' : 'shaft'
      };
    }
    
    // Fallback to legacy fields for migration
    return {
      propulsionType: vesselConfig.propulsionType || 'diesel',
      enginePosition: vesselConfig.enginePosition || 'inboard',
      engineLocation: vesselConfig.engineLocation || 'above_deck',
      method: vesselConfig.propulsionMethod || 'shaft'
    };
  };

  const details = getPropulsionDetails();
  
  // Check if fire protection choice should be shown
  const isPureBatteryElectric = details.propulsionType === 'battery_electric';
  const isOutboard = details.enginePosition === 'outboard';
  const isInboardAboveDeck = details.enginePosition === 'inboard' && details.engineLocation === 'above_deck';
  const isInboardBelowDeck = details.enginePosition === 'inboard' && details.engineLocation === 'below_deck';
  
  // Fire protection choice only for above deck inboard conventional/hybrid engines
  // Below deck = mandatory fixed system (no choice)
  // Pure battery electric = mandatory fixed system (no choice) 
  // Outboard = portable only (no choice)
  const showFireProtectionChoice = !isPureBatteryElectric && !isOutboard && isInboardAboveDeck;

  return (
    <>
      <div>
        <h3 className="text-base font-semibold text-gray-900">Engine & Propulsion</h3>
        <p className="mt-1 text-sm text-gray-600">Engine configuration affects maintenance requirements</p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-6">
        {/* Unified Propulsion Configuration - Replaces separate fields */}
        <PropulsionConfigurationSelector 
          vesselConfig={vesselConfig}
          updateConfig={updateConfig}
        />

        {/* Fire Protection Information - Updated for unified system */}
        <FireProtectionInformation
          showChoice={showFireProtectionChoice}
          isOutboard={isOutboard}
          isPureBatteryElectric={isPureBatteryElectric}
          isInboardBelowDeck={isInboardBelowDeck}
          currentPropulsionType={details.propulsionType}
          currentEngineLocation={details.engineLocation}
          vesselConfig={vesselConfig}
          updateConfig={updateConfig}
        />

        {/* Battery System Requirements - Updated for unified system */}
        <BatterySystemRequirements currentPropulsionType={details.propulsionType} />

        {/* Fuel System Configuration - for conventional engines only */}
        {!['battery_electric', 'battery_hybrid'].includes(details.propulsionType) && (
          <div className="sm:col-span-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-3">
                Fuel System Configuration
                <span className="ml-2 text-xs text-blue-600 font-normal">(for conventional engines)</span>
              </h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="fuelSystem" className="block text-sm font-medium text-blue-900">
                    Fuel System Type <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2">
                    <select
                      id="fuelSystem"
                      value={vesselConfig.fuelSystem || ''}
                      onChange={(e) => updateConfig({ fuelSystem: e.target.value })}
                      className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
                    >
                      <option value="">Select fuel system...</option>
                      <option value="gravity">Gravity Feed</option>
                      <option value="fuel_pump">Fuel Pump</option>
                      <option value="integral">Integral Tank</option>
                      <option value="other">Other (specify)</option>
                    </select>
                  </div>
                  <p className="mt-1 text-xs text-blue-700">
                    {vesselConfig.fuelSystem === 'gravity'
                      ? 'Fuel flows by gravity - requires tank position above engine'
                      : vesselConfig.fuelSystem === 'fuel_pump'
                        ? 'Electric or mechanical fuel pump - maintenance required'
                      : vesselConfig.fuelSystem === 'integral'
                        ? 'Small engine with built-in tank (<4.5kW) - minimal maintenance'
                      : 'Select fuel system type for maintenance requirements'}
                  </p>
                </div>
                
                {/* Other fuel system specification */}
                {vesselConfig.fuelSystem === 'other' && (
                  <div>
                    <label htmlFor="otherFuelSystem" className="block text-sm font-medium text-blue-900">
                      Specify Fuel System Type <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        id="otherFuelSystem"
                        value={vesselConfig.otherFuelSystem || ''}
                        onChange={(e) => updateConfig({ otherFuelSystem: e.target.value })}
                        placeholder="e.g., High pressure injection, Common rail, etc."
                        className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
                      />
                    </div>
                    <p className="mt-1 text-xs text-blue-700">Describe your fuel system type</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="sm:col-span-2">
          <label htmlFor="numberOfEngines" className="block text-sm font-medium text-gray-900">
            Number of {details.propulsionType === 'battery_electric' ? 'Motors' : 
                      details.propulsionType === 'battery_hybrid' ? 'Engines/Motors' : 'Engines'}
          </label>
          <div className="mt-2">
            <input
              type="number"
              id="numberOfEngines"
              value={vesselConfig.numberOfEngines}
              onChange={(e) => updateConfig({ numberOfEngines: parseInt(e.target.value) || 1 })}
              placeholder="e.g., 2"
              min="1"
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            />
          </div>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="enginePower" className="block text-sm font-medium text-gray-900">
            Total {details.propulsionType === 'battery_electric' ? 'Motor' : 
                  details.propulsionType === 'battery_hybrid' ? 'Engine/Motor' : 'Engine'} Power (kW)
          </label>
          <div className="mt-2">
            <input
              type="number"
              id="enginePower"
              value={vesselConfig.enginePower || ''}
              onChange={(e) => AutoConfig.handleEnginePowerChange(e.target.value, vesselConfig, updateConfig)}
              placeholder="e.g., 150"
              min="0"
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">Affects fire extinguisher quantity requirements</p>
        </div>

        {/* Maximum Speed and High-Speed Detection */}
        <div className="sm:col-span-2">
          <label htmlFor="maxSpeed" className="block text-sm font-medium text-gray-900">
            Maximum Speed (knots) <span className="text-red-500">*</span>
          </label>
          <div className="mt-2">
            <input
              type="number"
              id="maxSpeed"
              value={vesselConfig.maxSpeed || ''}
              onChange={(e) => updateConfig({ maxSpeed: parseFloat(e.target.value) || '' })}
              placeholder="e.g., 25"
              min="0"
              step="0.1"
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">Maximum operational speed</p>
          {vesselConfig.maxSpeed >= 20 && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800 font-medium">
                ⚠️ High-Speed Classification: Enhanced equipment and operational requirements apply
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Vessels ≥20 knots require additional safety equipment and crew training per WBC3
              </p>
            </div>
          )}
        </div>


        <div className="sm:col-span-3">
          <label htmlFor="steeringSystem" className="block text-sm font-medium text-gray-900">
            Steering Control Type <span className="text-red-500">*</span>
          </label>
          <div className="mt-2">
            <select
              id="steeringSystem"
              value={vesselConfig.steeringSystem || vesselConfig.steeringType || ''}
              onChange={(e) => updateConfig({ 
                steeringSystem: e.target.value,
                steeringType: e.target.value // Backward compatibility
              })}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            >
              <option value="">Select steering system...</option>
              <option value="hydraulic">Hydraulic</option>
              <option value="cable">Cable/Mechanical</option>
              <option value="electric">Electric</option>
              <option value="rudder">Rudder (Direct)</option>
              {/* Only show tiller for small vessels */}
              {vesselConfig.lengthOverall && vesselConfig.lengthOverall < 12 && (
                <option value="tiller">Tiller (vessels &lt;12m only)</option>
              )}
              {/* Only show remote control for unmanned vessels */}
              {vesselConfig.vesselType === 'unmanned' && (
                <option value="remote">Remote Control (ROUV only)</option>
              )}
            </select>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {vesselConfig.steeringSystem === 'remote'
              ? 'Remote control - Emergency steering required per WBC3 10.2.3 (drill demonstration required)'
              : (vesselConfig.steeringSystem === 'hydraulic' || vesselConfig.steeringType === 'hydraulic' ||
                 vesselConfig.steeringSystem === 'electric' || vesselConfig.steeringType === 'electric' ||
                 vesselConfig.steeringSystem === 'cable' ||
                 vesselConfig.steeringSystem === 'rudder' || vesselConfig.steeringType === 'rudder' ||
                 vesselConfig.steeringSystem === 'tiller')
                ? 'Direct control system - No emergency steering requirements per WBC3 10.2'
              : 'Select steering type to determine requirements'}
          </p>
        </div>

        {/* NEW CRITICAL FIELDS */}
        <div className="sm:col-span-3">
          <label htmlFor="steeringPosition" className="block text-sm font-medium text-gray-900">
            Steering Position <span className="text-red-500">*</span>
          </label>
          <div className="mt-2">
            <select
              id="steeringPosition"
              value={vesselConfig.steeringPosition || ''}
              onChange={(e) => updateConfig({ steeringPosition: e.target.value })}
              required
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            >
              <option value="">Select steering position...</option>
              <option value="open">Open steering position</option>
              <option value="enclosed">Enclosed wheelhouse</option>
              <option value="both">Both open and enclosed</option>
            </select>
          </div>
          <p className="mt-1 text-xs text-gray-500">Kill cord required for open positions</p>
        </div>

        <div className="sm:col-span-3">
          <label htmlFor="engineStartMethod" className="block text-sm font-medium text-gray-900">
            Engine Starting Method <span className="text-red-500">*</span>
          </label>
          <div className="mt-2">
            <select
              id="engineStartMethod"
              value={vesselConfig.engineStartMethod || ''}
              onChange={(e) => updateConfig({ engineStartMethod: e.target.value })}
              required
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            >
              <option value="">Select starting method...</option>
              <option value="electric">Electric start</option>
              <option value="manual">Manual/hand start</option>
              <option value="air">Air start</option>
              <option value="hydraulic">Hydraulic start</option>
            </select>
          </div>
          <p className="mt-1 text-xs text-gray-500">Determines backup power requirements</p>
        </div>

        <div className="sm:col-span-3">
          <label htmlFor="steeringControlType" className="block text-sm font-medium text-gray-900">
            Steering Control Type <span className="text-red-500">*</span>
          </label>
          <div className="mt-2">
            <select
              id="steeringControlType"
              value={vesselConfig.steeringControlType || ''}
              onChange={(e) => updateConfig({ steeringControlType: e.target.value })}
              required
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            >
              <option value="">Select control type...</option>
              <option value="direct">Direct mechanical (tiller/wheel)</option>
              <option value="hydraulic">Hydraulic steering</option>
              <option value="cable">Cable/teleflex</option>
              <option value="electronic">Electronic/fly-by-wire</option>
            </select>
          </div>
          <p className="mt-1 text-xs text-gray-500">For emergency steering procedures</p>
        </div>

        {/* Show oil lubricated shaft option only for pilot boats with shaft drive */}
        {(details.method === 'shaft' || vesselConfig.propulsionMethod === 'shaft' || vesselConfig.propulsionType === 'shaft') && 
         (vesselConfig.vesselType === 'pilot' || vesselConfig.type === 'pilot' || vesselConfig.hasWorkboatPilotEndorsement) && (
          <div className="sm:col-span-3">
            <div className="relative flex items-start">
              <div className="flex h-6 items-center">
                <input
                  id="hasOilLubricatedShaft"
                  type="checkbox"
                  checked={vesselConfig.hasOilLubricatedShaft || false}
                  onChange={(e) => updateConfig({ hasOilLubricatedShaft: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="hasOilLubricatedShaft" className="font-medium text-gray-900">
                  Oil Lubricated Shaft
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  If checked, annual oil analysis records required (WBC3 4.10.3). 
                  Otherwise shaft examination every 3 years (WBC3 4.10.1).
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EnginePropulsionSection;